import type { DocusignSignatureResponse } from '@/services/proposals/types';
import {
  mapEnvelopeStatusLabel,
  mapRecipientStatusLabel,
} from '@/features/proposals/components/capital-global/utils/capital-global-docusign-signature.util';
import { Button, Text } from '@noorden-insurtech/alba-design-system';

type Props = {
  signature: DocusignSignatureResponse;
  isViewMode: boolean;
  canSync: boolean;
  canRetry: boolean;
  isSyncPending: boolean;
  isRetryPending: boolean;
  onSync: () => void;
  onRetry: () => void;
  /** Trava externa (ex.: corretora inativa): desabilita com o motivo no title. */
  actionsDisabledReason?: string;
  actionsDisabled?: boolean;
};

function RecipientProgressRow({
  role,
  name,
  email,
  status,
}: {
  role: string;
  name: string;
  email: string;
  status: string;
}) {
  return (
    <div className='flex flex-col gap-1 rounded-xl border border-alba-gray-200 bg-alba-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between'>
      <div className='flex flex-col gap-0.5 text-left'>
        <Text className='text-xs font-medium uppercase tracking-wide text-alba-gray-500'>
          {role}
        </Text>
        <Text className='text-sm font-semibold text-alba-gray-900'>{name}</Text>
        <Text className='text-xs text-alba-gray-600'>{email}</Text>
      </div>
      <Text className='text-sm font-medium text-alba-gray-800'>
        {mapRecipientStatusLabel(status)}
      </Text>
    </div>
  );
}

export function CapitalGlobalProposalDocusignStatusSection({
  signature,
  isViewMode,
  canSync,
  canRetry,
  isSyncPending,
  isRetryPending,
  onSync,
  onRetry,
  actionsDisabledReason,
  actionsDisabled = false,
}: Props) {
  return (
    <div className='flex flex-col gap-4 rounded-2xl border border-alba-gray-200 bg-white p-6 shadow-sm'>
      <div className='flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between'>
        <div className='flex flex-col gap-1'>
          <Text className='text-lg font-semibold text-alba-gray-900'>
            Assinatura digital
          </Text>
          <Text className='text-sm text-alba-gray-600'>
            {mapEnvelopeStatusLabel(signature.status, signature.failureSubtype)}
          </Text>
        </div>
        {!isViewMode && (canSync || canRetry) && (
          <div className='flex flex-wrap gap-2 pt-2 sm:pt-0'>
            {canSync && (
              <Button
                type='button'
                size='sm'
                variant='secondary'
                onClick={onSync}
                disabled={isSyncPending || isRetryPending || actionsDisabled}
                title={actionsDisabledReason}
                loading={isSyncPending}
              >
                Verificar assinatura
              </Button>
            )}
            {canRetry && (
              <Button
                type='button'
                size='sm'
                variant='primary'
                onClick={onRetry}
                disabled={isSyncPending || isRetryPending || actionsDisabled}
                title={actionsDisabledReason}
                loading={isRetryPending}
              >
                Tentar novamente
              </Button>
            )}
          </div>
        )}
      </div>

      <RecipientProgressRow
        role='Estipulante'
        name={signature.stipulatorName}
        email={signature.stipulatorEmail}
        status={signature.stipulatorStatus}
      />
      <RecipientProgressRow
        role='Corretor'
        name={signature.brokerName}
        email={signature.brokerEmail}
        status={signature.brokerStatus}
      />

      {signature.status === 'completed' && (
        <Text className='text-sm text-alba-green-700'>
          Assinatura concluída. Os documentos assinados foram registrados na
          proposta.
        </Text>
      )}
    </div>
  );
}
